import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listAdminLayouts,
  listAdminFamilies,
  getExtractionAvailability,
  updateLayout,
  deleteLayout,
  type AdminLayout,
  type AdminFamily,
} from '../../lib/adminApi';
import { formatCode, formatCents, formatDimensions } from '../../lib/format';
import { AdminPageShell, cls, ChevronDown } from './shared';
import { NewLayoutPanel } from './NewLayoutPanel';

export function LayoutsPage() {
  const [layouts, setLayouts] = useState<AdminLayout[]>([]);
  const [families, setFamilies] = useState<AdminFamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterFamily, setFilterFamily] = useState<number | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  // Whether the server can read a pasted catalog page. False (no ANTHROPIC_API_KEY)
  // just means the create panel opens straight on manual entry.
  const [extractionAvailable, setExtractionAvailable] = useState(false);

  function load() {
    Promise.all([listAdminLayouts(), listAdminFamilies()])
      .then(([lr, fr]) => {
        setLayouts(lr.layouts);
        setFamilies(fr.families);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to load');
        setLoading(false);
      });
  }

  useEffect(() => {
    load();
    // Non-fatal: on failure we simply offer manual entry only.
    getExtractionAvailability()
      .then((r) => setExtractionAvailable(r.available))
      .catch(() => setExtractionAvailable(false));
  }, []);

  const visible = filterFamily ? layouts.filter((l) => l.familyId === filterFamily) : layouts;

  if (loading) return <AdminPageShell title="Layouts"><p className="text-vari-gray text-sm">Loading…</p></AdminPageShell>;
  if (error) return <AdminPageShell title="Layouts"><p className="text-red-600 text-sm">{error}</p></AdminPageShell>;

  // The filter row is pinned while browsing, but NOT while the create panel is open:
  // that panel can grow taller than the scroll container (20 BOM rows, or an expanded
  // source page), and a `position: sticky` block taller than its container can never be
  // scrolled to — the Create button ends up permanently off-screen. With the panel open
  // the whole page scrolls normally instead.
  const filterRow = (
    <div className="flex items-center gap-3">
      <label className="text-sm text-vari-gray">Filter family:</label>
      <div className="relative inline-flex items-center">
        <select
          className={cls.select + ' appearance-none pr-7'}
          value={filterFamily ?? ''}
          onChange={(e) => setFilterFamily(e.target.value ? parseInt(e.target.value, 10) : null)}
        >
          <option value="">All</option>
          {families.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-2 h-3 w-3 text-vari-gray/40 pointer-events-none" />
      </div>
      <span className="text-xs text-vari-gray ml-auto">{visible.length} shown</span>
    </div>
  );

  return (
    <AdminPageShell
      title={`Layouts (${layouts.length})`}
      actions={
        <button
          onClick={() => setShowCreate((v) => !v)}
          className={cls.btnPrimary}
        >
          + New Layout
        </button>
      }
      stickyTop={showCreate ? undefined : filterRow}
    >
      {showCreate && (
        <>
          <NewLayoutPanel
            families={families}
            extractionAvailable={extractionAvailable}
            onCreated={load}
            onCancel={() => setShowCreate(false)}
          />
          <div className="mb-4">{filterRow}</div>
        </>
      )}

      <div className={cls.tableWrap}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-vari-soft-gray">
              <tr>
                <th className={cls.th}>Code</th>
                <th className={cls.th}>Family</th>
                <th className={cls.th}>Name</th>
                <th className={cls.th}>Dimensions</th>
                <th className={cls.th}>Price</th>
                <th className={cls.thCenter}>Active</th>
                <th className={cls.th}>Renders</th>
                <th className={cls.th}>BOM</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {visible.map((l, i) => (
                <LayoutRow
                  key={l.id}
                  layout={l}
                  families={families}
                  striped={i % 2 === 1}
                  onSaved={load}
                />
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-6 text-center text-vari-gray text-sm">
                    No layouts.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminPageShell>
  );
}

function LayoutRow({
  layout,
  families,
  striped,
  onSaved,
}: {
  layout: AdminLayout;
  families: AdminFamily[];
  striped: boolean;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Name editing
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(layout.name ?? '');

  // Dimensions editing
  const [editingDims, setEditingDims] = useState(false);
  const [widthValue, setWidthValue] = useState(layout.widthInches?.toString() ?? '');
  const [depthValue, setDepthValue] = useState(layout.depthInches?.toString() ?? '');

  async function save(updates: Parameters<typeof updateLayout>[1]) {
    setSaving(true);
    try {
      await updateLayout(layout.id, updates);
      onSaved();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive() {
    await save({ is_active: !layout.isActive });
  }

  async function commitName() {
    setEditingName(false);
    const trimmed = nameValue.trim();
    if (trimmed !== (layout.name ?? '')) {
      await save({ name: trimmed || null });
    }
  }

  async function commitDims() {
    setEditingDims(false);
    const w = widthValue.trim() ? parseFloat(widthValue) : null;
    const d = depthValue.trim() ? parseFloat(depthValue) : null;
    if (w !== layout.widthInches || d !== layout.depthInches) {
      await save({ width_inches: w, depth_inches: d });
    }
  }

  async function handleDelete() {
    try {
      await deleteLayout(layout.id);
      onSaved();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to delete');
      setConfirmDelete(false);
    }
  }

  const cellBase = 'px-3 py-2.5 text-xs';

  return (
    <tr className={`${cls.tr(striped)} ${!layout.isActive ? 'opacity-50' : ''}`}>
      <td className={`${cellBase} font-mono`}>
        <Link
          to={`/admin/layouts/${layout.id}/renders`}
          state={{ code: layout.code }}
          className="text-vari-blue-accessible hover:underline"
        >
          {formatCode(layout.code)}
        </Link>
      </td>

      {/* Family — select dropdown */}
      <td className={cellBase}>
        <div className="relative inline-flex items-center gap-1">
          <select
            className="appearance-none text-xs text-vari-gray bg-transparent border-none cursor-pointer pr-4 focus:outline-none hover:text-vari-navy transition-colors disabled:cursor-not-allowed"
            value={layout.familyId}
            disabled={saving}
            onChange={(e) => void save({ family_id: parseInt(e.target.value, 10) })}
          >
            {families.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-0 h-3 w-3 text-vari-gray/40 pointer-events-none" />
        </div>
      </td>

      {/* Name — click to edit */}
      <td className={cellBase}>
        {editingName ? (
          <input
            autoFocus
            type="text"
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={() => void commitName()}
            onKeyDown={(e) => { if (e.key === 'Enter') void commitName(); if (e.key === 'Escape') { setNameValue(layout.name ?? ''); setEditingName(false); } }}
            className="text-xs border border-vari-soft-gray rounded px-1 py-0.5 w-44 focus:outline focus:outline-1 focus:outline-vari-navy"
          />
        ) : (
          <span
            onClick={() => { setNameValue(layout.name ?? ''); setEditingName(true); }}
            className="cursor-pointer hover:bg-gray-100 rounded px-1 -ml-1"
            title="Click to edit"
          >
            {layout.name ?? <span className="text-vari-gray/50 italic">—</span>}
          </span>
        )}
      </td>

      {/* Dimensions — click to edit */}
      <td className={`${cellBase} text-vari-gray`}>
        {editingDims ? (
          <span className="flex items-center gap-1">
            <input
              autoFocus
              type="number"
              min="0"
              step="0.5"
              value={widthValue}
              onChange={(e) => setWidthValue(e.target.value)}
              onBlur={() => void commitDims()}
              onKeyDown={(e) => { if (e.key === 'Enter') void commitDims(); if (e.key === 'Escape') { setWidthValue(layout.widthInches?.toString() ?? ''); setDepthValue(layout.depthInches?.toString() ?? ''); setEditingDims(false); } }}
              placeholder="W"
              className="text-xs border border-vari-soft-gray rounded px-1 py-0.5 w-14 focus:outline focus:outline-1 focus:outline-vari-navy"
            />
            <span className="text-vari-gray/50">×</span>
            <input
              type="number"
              min="0"
              step="0.5"
              value={depthValue}
              onChange={(e) => setDepthValue(e.target.value)}
              onBlur={() => void commitDims()}
              onKeyDown={(e) => { if (e.key === 'Enter') void commitDims(); if (e.key === 'Escape') { setWidthValue(layout.widthInches?.toString() ?? ''); setDepthValue(layout.depthInches?.toString() ?? ''); setEditingDims(false); } }}
              placeholder="D"
              className="text-xs border border-vari-soft-gray rounded px-1 py-0.5 w-14 focus:outline focus:outline-1 focus:outline-vari-navy"
            />
            <span className="text-vari-gray/60 text-xs">in</span>
          </span>
        ) : (
          <span
            onClick={() => { setWidthValue(layout.widthInches?.toString() ?? ''); setDepthValue(layout.depthInches?.toString() ?? ''); setEditingDims(true); }}
            className="cursor-pointer hover:bg-gray-100 rounded px-1 -ml-1"
            title="Click to edit"
          >
            {formatDimensions(layout.widthInches, layout.depthInches)}
          </span>
        )}
      </td>

      <td className={cellBase}>{formatCents(layout.totalPriceCents)}</td>
      <td className="px-3 py-2.5 text-center">
        <input
          type="checkbox"
          checked={layout.isActive}
          disabled={saving}
          onChange={() => void toggleActive()}
          className="accent-vari-navy cursor-pointer"
        />
      </td>
      <td className="px-3 py-2.5">
        <Link
          to={`/admin/layouts/${layout.id}/renders`}
          className="text-xs text-vari-blue-accessible hover:underline"
        >
          {layout.renderCount} renders
        </Link>
      </td>
      <td className="px-3 py-2.5">
        <Link
          to={`/admin/layouts/${layout.id}/bom`}
          className="text-xs text-vari-blue-accessible hover:underline"
        >
          {layout.bomCount} items
        </Link>
      </td>
      <td className="px-3 py-2.5 text-right">
        {confirmDelete ? (
          <span className="space-x-1">
            <button
              onClick={() => void handleDelete()}
              className={cls.btnConfirm}
            >
              Confirm?
            </button>
            <button onClick={() => setConfirmDelete(false)} className={cls.btnGhost}>
              Cancel
            </button>
          </span>
        ) : (
          <button
            onClick={() => setConfirmDelete(true)}
            disabled={layout.isActive}
            className={`${cls.btnDanger} disabled:opacity-30 disabled:cursor-not-allowed`}
            title={layout.isActive ? 'Deactivate before deleting' : undefined}
          >
            Delete
          </button>
        )}
      </td>
    </tr>
  );
}
